import React from "react";
import { Link } from "react-router-dom";
import type { AppLanguage } from "locales";

/**
 * Terms of Use content per language. JSX trees keep <Link>/<strong>/<ul>
 * first-class — same pattern as PagePrivacy.
 */
export interface LegalContent {
  title: string;
  lastUpdated: string;
  body: React.ReactNode;
}

const en: LegalContent = {
  title: "Terms of Use",
  lastUpdated: "Last updated: April 10, 2026",
  body: (
    <>
      <section>
        <h2>1. Acceptance of Terms</h2>
        <p>
          By accessing or using the SMeet platform (&ldquo;Service&rdquo;), operated by Scrile Inc. (&ldquo;Company&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;),
          you agree to be bound by these Terms of Use. If you do not agree, do not use the Service.
        </p>
      </section>

      <section>
        <h2>2. Description of Service</h2>
        <p>
          SMeet is a corporate messaging and collaboration platform that provides:
        </p>
        <ul>
          <li>Instant messaging (personal and group chats)</li>
          <li>HD video and audio calls via WebRTC</li>
          <li>File sharing (up to 2 GB per file)</li>
          <li>Chat folders and organization tools</li>
          <li>Voice messages</li>
        </ul>
      </section>

      <section>
        <h2>3. Account Registration</h2>
        <p>
          To use certain features, you must create an account. You agree to provide accurate, current,
          and complete information during registration and to keep your account credentials secure.
          You are responsible for all activity under your account.
        </p>
        <p>
          You must be at least 18 years old to create an account.
        </p>
      </section>

      <section>
        <h2>4. Acceptable Use</h2>
        <p>You agree not to:</p>
        <ul>
          <li>Use the Service for any illegal or unauthorized purpose</li>
          <li>Transmit spam, malware, or harmful content</li>
          <li>Attempt to gain unauthorized access to the Service or other users&apos; accounts</li>
          <li>Interfere with or disrupt the integrity of the Service</li>
          <li>Impersonate another person or entity</li>
          <li>Collect or harvest personal data of other users without consent</li>
          <li>Upload content that infringes on intellectual property rights</li>
        </ul>
      </section>

      <section>
        <h2>5. User Content</h2>
        <p>
          You retain ownership of content you upload or share through the Service (&ldquo;User Content&rdquo;).
          By uploading User Content, you grant us a limited, non-exclusive license to store, process,
          and deliver that content as necessary to operate the Service.
        </p>
        <p>
          You are solely responsible for the content you share. We do not monitor or endorse User Content
          but reserve the right to remove content that violates these Terms.
        </p>
      </section>

      <section>
        <h2>6. Privacy</h2>
        <p>
          Your use of the Service is subject to our <Link to="/privacy">Privacy Policy</Link>,
          which describes how we collect, use, and protect your personal information.
        </p>
      </section>

      <section>
        <h2>7. Intellectual Property</h2>
        <p>
          The Service, including its design, code, logos, and trademarks, is the property of Scrile Inc.
          and is protected by intellectual property laws. You may not copy, modify, distribute, or
          reverse-engineer any part of the Service without our written consent.
        </p>
      </section>

      <section>
        <h2>8. Third-Party Services</h2>
        <p>
          The Service may integrate with or link to third-party services. We are not responsible for
          the availability, content, or practices of those services. Your use of third-party services
          is at your own risk and subject to their terms.
        </p>
      </section>

      <section>
        <h2>9. Termination</h2>
        <p>
          We may suspend or terminate your account at our discretion if you violate these Terms
          or engage in activity harmful to the Service or other users. You may delete your account
          at any time through Settings.
        </p>
        <p>
          Upon termination, your right to use the Service ceases immediately. We may retain
          certain data as required by law or for legitimate business purposes.
        </p>
      </section>

      <section>
        <h2>10. Disclaimers</h2>
        <p>
          The Service is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo; without warranties of any kind,
          whether express or implied, including but not limited to warranties of merchantability,
          fitness for a particular purpose, or non-infringement.
        </p>
        <p>
          We do not guarantee uninterrupted, secure, or error-free operation of the Service.
        </p>
      </section>

      <section>
        <h2>11. Limitation of Liability</h2>
        <p>
          To the maximum extent permitted by law, Scrile Inc. shall not be liable for any indirect,
          incidental, special, consequential, or punitive damages arising from your use of the Service,
          including but not limited to loss of data, profits, or business opportunities.
        </p>
      </section>

      <section>
        <h2>12. Changes to Terms</h2>
        <p>
          We may update these Terms from time to time. We will notify users of material changes
          by posting the updated Terms on this page with a revised &ldquo;Last updated&rdquo; date.
          Continued use of the Service after changes constitutes acceptance of the new Terms.
        </p>
      </section>

      <section>
        <h2>13. Governing Law</h2>
        <p>
          These Terms are governed by and construed in accordance with the laws of the State of Delaware,
          United States, without regard to its conflict of law provisions.
        </p>
      </section>

      <section>
        <h2>14. Contact Us</h2>
        <p>
          If you have questions about these Terms, contact us at:
        </p>
        <p>
          <strong>Scrile Inc.</strong><br />
          Email: support@scrile.com<br />
          Website: <a href="https://www.scrile.com" target="_blank" rel="noreferrer">www.scrile.com</a>
        </p>
      </section>
    </>
  ),
};

const ru: LegalContent = {
  title: "Условия использования",
  lastUpdated: "Обновлено: 10 апреля 2026",
  body: (
    <>
      <section>
        <h2>1. Принятие условий</h2>
        <p>
          Получая доступ к платформе SMeet (далее «Сервис»), которую обслуживает Scrile Inc.
          (далее «Компания», «мы»), либо используя её, вы соглашаетесь соблюдать настоящие Условия
          использования. Если вы не согласны — не используйте Сервис.
        </p>
      </section>

      <section>
        <h2>2. Описание Сервиса</h2>
        <p>
          SMeet — корпоративная платформа обмена сообщениями и совместной работы, которая предоставляет:
        </p>
        <ul>
          <li>мгновенный обмен сообщениями (личные и групповые чаты);</li>
          <li>HD-видео- и аудиозвонки через WebRTC;</li>
          <li>обмен файлами (до 2 ГБ на файл);</li>
          <li>папки чатов и инструменты организации;</li>
          <li>голосовые сообщения.</li>
        </ul>
      </section>

      <section>
        <h2>3. Регистрация аккаунта</h2>
        <p>
          Для использования отдельных функций необходимо создать аккаунт. Вы обязуетесь предоставлять
          точные, актуальные и полные данные при регистрации и обеспечивать конфиденциальность учётных
          данных. Вы несёте ответственность за всю активность под вашим аккаунтом.
        </p>
        <p>
          Чтобы создать аккаунт, вам должно быть не менее 18 лет.
        </p>
      </section>

      <section>
        <h2>4. Правила использования</h2>
        <p>Вы обязуетесь не:</p>
        <ul>
          <li>использовать Сервис в любых незаконных или несанкционированных целях;</li>
          <li>передавать спам, вредоносное ПО или вредный контент;</li>
          <li>пытаться получить несанкционированный доступ к Сервису или аккаунтам других пользователей;</li>
          <li>мешать работе Сервиса или нарушать его целостность;</li>
          <li>выдавать себя за другое лицо или организацию;</li>
          <li>собирать персональные данные других пользователей без согласия;</li>
          <li>загружать контент, нарушающий права интеллектуальной собственности.</li>
        </ul>
      </section>

      <section>
        <h2>5. Пользовательский контент</h2>
        <p>
          Вы сохраняете право собственности на контент, который загружаете или распространяете через
          Сервис («Пользовательский контент»). Загружая Пользовательский контент, вы предоставляете
          нам ограниченную неисключительную лицензию на его хранение, обработку и доставку в объёме,
          необходимом для работы Сервиса.
        </p>
        <p>
          Вы единолично отвечаете за контент, которым делитесь. Мы не модерируем и не одобряем
          Пользовательский контент, но оставляем за собой право удалять контент, нарушающий настоящие
          Условия.
        </p>
      </section>

      <section>
        <h2>6. Конфиденциальность</h2>
        <p>
          Ваше использование Сервиса регулируется нашей <Link to="/privacy">Политикой конфиденциальности</Link>,
          которая описывает, как мы собираем, используем и защищаем ваши персональные данные.
        </p>
      </section>

      <section>
        <h2>7. Интеллектуальная собственность</h2>
        <p>
          Сервис, включая его дизайн, код, логотипы и товарные знаки, является собственностью Scrile Inc.
          и охраняется законами об интеллектуальной собственности. Вы не имеете права копировать,
          модифицировать, распространять или реверс-инжинирить какие-либо части Сервиса без нашего
          письменного согласия.
        </p>
      </section>

      <section>
        <h2>8. Сторонние сервисы</h2>
        <p>
          Сервис может интегрироваться со сторонними сервисами или содержать ссылки на них. Мы не несём
          ответственности за доступность, содержание или практики таких сервисов. Использование сторонних
          сервисов осуществляется на ваш страх и риск и регулируется их условиями.
        </p>
      </section>

      <section>
        <h2>9. Прекращение действия</h2>
        <p>
          Мы можем приостановить или прекратить действие вашего аккаунта по нашему усмотрению, если вы
          нарушаете настоящие Условия или ведёте деятельность, наносящую вред Сервису или другим
          пользователям. Вы можете удалить свой аккаунт в любое время через Настройки.
        </p>
        <p>
          После прекращения действия ваше право использовать Сервис прекращается немедленно. Часть данных
          может быть сохранена по требованиям закона или для законных деловых целей.
        </p>
      </section>

      <section>
        <h2>10. Оговорки</h2>
        <p>
          Сервис предоставляется «как есть» и «по мере доступности» без каких-либо явных или подразумеваемых
          гарантий, включая, помимо прочего, гарантии товарного качества, пригодности для конкретной цели
          или отсутствия нарушений.
        </p>
        <p>
          Мы не гарантируем непрерывную, безопасную или безошибочную работу Сервиса.
        </p>
      </section>

      <section>
        <h2>11. Ограничение ответственности</h2>
        <p>
          В максимально допустимой законом мере Scrile Inc. не несёт ответственности за любые косвенные,
          случайные, специальные, последующие или штрафные убытки, возникающие в связи с использованием
          Сервиса, включая, помимо прочего, потерю данных, прибыли или коммерческих возможностей.
        </p>
      </section>

      <section>
        <h2>12. Изменения условий</h2>
        <p>
          Мы можем периодически обновлять настоящие Условия. О существенных изменениях мы уведомим
          пользователей, разместив обновлённую версию на этой странице с новой датой «Обновлено».
          Продолжение использования Сервиса после изменений означает согласие с новыми Условиями.
        </p>
      </section>

      <section>
        <h2>13. Применимое право</h2>
        <p>
          Настоящие Условия регулируются и толкуются в соответствии с законодательством штата Делавэр,
          США, без учёта норм коллизионного права.
        </p>
      </section>

      <section>
        <h2>14. Контакты</h2>
        <p>
          Если у вас есть вопросы по настоящим Условиям, свяжитесь с нами:
        </p>
        <p>
          <strong>Scrile Inc.</strong><br />
          Email: support@scrile.com<br />
          Сайт: <a href="https://www.scrile.com" target="_blank" rel="noreferrer">www.scrile.com</a>
        </p>
      </section>
    </>
  ),
};

const catalogue: Record<AppLanguage, LegalContent> = { ru, en };

export function getTermsContent(lang: AppLanguage): LegalContent {
  return catalogue[lang] ?? catalogue.en;
}
